<script setup>
import { computed, onMounted, onUpdated, ref } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Expandable tree of nested items, one of which can be selected.
 *
 * Each node may carry an `icon`; its children sit under `children`.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Picking one place in a hierarchy, such as the folder to put something in
 * @avoidWhen The items are flat — use HrListSelect
 * @related HrListSelect
 * @a11y Nodes expand and select from the keyboard with Enter and Space. Name the tree with `aria-label`;
 * each node reports whether it is selected.
 */
defineOptions({ name: 'HrTree' });

const props = defineProps({
  /**
   * The root nodes, each with its children nested under `children`.
   * @example [{"id":"root","label":"Library","children":[{"id":"a","label":"Programs","icon":"folder"}]}]
   */
  nodes: {
    type: Array,
    required: true,
  },
  /** Property that identifies a node; also the value `selected` holds. */
  nodeKey: {
    type: String,
    default: 'id',
  },
  /** Property holding a node's label. */
  labelKey: {
    type: String,
    default: 'label',
  },
  /** Key of the selected node, or null. Supports v-model:selected. */
  selected: {
    type: [String, Number],
    default: null,
  },
  /** Keys of the expanded nodes. Supports v-model:expanded. */
  expanded: {
    type: Array,
    default: () => [],
  },
  /**
   * Legacy props forwarded to q-tree when the legacy theme is active. Ignored under the
   * design-system theme.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted with the newly selected node's key. */
  'update:selected': null,
  /** Emitted with the keys of the expanded nodes. */
  'update:expanded': null,
});

const { hasTheme } = useTheme();

const root = ref(null);

// q-tree marks the selected node only with a class, so screen readers would not hear which one it is.
function syncSelectedState() {
  root.value?.$el.querySelectorAll('.q-tree__node-header').forEach((header) => {
    header.setAttribute('aria-selected', String(header.classList.contains('q-tree__node--selected')));
  });
}

onMounted(syncSelectedState);
onUpdated(syncSelectedState);

const effectiveProps = computed(() => (hasTheme.value
  ? {}
  : { selectedColor: 'primary', ...props.legacyProps }));
</script>

<template>
  <q-tree
    ref="root"
    v-bind="effectiveProps"
    :nodes="nodes"
    :node-key="nodeKey"
    :label-key="labelKey"
    :selected="selected"
    :expanded="expanded"
    :class="hasTheme ? 'hr-tree' : undefined"
    @update:selected="emit('update:selected', $event)"
    @update:expanded="emit('update:expanded', $event)"
  />
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-tree {
  color: var(--app-on-surface);
}

.hr-tree :deep(.q-tree__node-header) {
  border-radius: var(--app-shape-corner-full);
}

/* Quasar fixes the label to black, and to grey once selected. */
.hr-tree :deep(.q-tree__node-header-content) {
  color: var(--app-on-surface);
}

.hr-tree :deep(.q-tree__node-header .q-icon) {
  color: var(--app-on-surface-variant);
}

.hr-tree :deep(.q-tree__node--selected) {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-tree :deep(.q-tree__node--selected .q-tree__node-header-content),
.hr-tree :deep(.q-tree__node--selected .q-icon) {
  color: var(--app-on-secondary-container);
}

.hr-tree :deep(.q-tree__node::after),
.hr-tree :deep(.q-tree__node-header::before) {
  border-color: var(--app-outline-variant);
}

</style>
